import type {
  ReactNode,
  ChangeEvent,
  SelectHTMLAttributes,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  HTMLAttributes
} from 'react'
import { useState } from 'react'
import classes from './styles/combineStyles'
import { BtnSC, Err, InputSC, LabelSC as Label, Radio, RadioLabel, SelectPlaceholder, SelectSC } from './styles/input'

export const ErrorMsg = ({ children }: { children?: ReactNode }) =>
  children ? <Err aria-live="polite">{children}</Err> : null

type SelectProps = {
  label?: string
  error?: boolean
  errorMsg?: string
  placeholder?: string
  onChange: (evt: ChangeEvent<HTMLSelectElement>) => void
  options?: {
    value: string | number
    text: ReactNode
  }[]
  className?: string
} & SelectHTMLAttributes<HTMLSelectElement>
const Select = ({ label, error, errorMsg, placeholder, onChange, ...attrs }: SelectProps) => {
  const [showPlaceholder, setShowPlaceholder] = useState(!attrs.value)

  const onSelectChange = evt => {
    setShowPlaceholder(!evt.target.value)
    onChange(evt)
  }

  return (
    <>
      {label ? <Label htmlFor={attrs.id}>{label}</Label> : null}
      <SelectSC {...attrs} className={errorMsg || error ? 'errorField' : undefined} onChange={onSelectChange} />
      {placeholder && showPlaceholder ? <SelectPlaceholder>{placeholder}</SelectPlaceholder> : null}
      <ErrorMsg>{errorMsg}</ErrorMsg>
    </>
  )
}

type ButtonProps = {
  children: ReactNode
  type?: 'button' | 'submit' | 'reset'
  className?: string
  orange?: boolean
  loading?: boolean
} & ButtonHTMLAttributes<HTMLButtonElement>
const Button = ({ children, type = 'submit', className, orange, ...attrs }: ButtonProps) => (
  <BtnSC className={classes(className, orange && 'orange')} type={type} {...attrs}>
    {children}
  </BtnSC>
)

type InputProps = {
  className?: string
  joined?: boolean
  label?: string
  hideLabel?: boolean
  error?: boolean
  errorMsg?: string
} & InputHTMLAttributes<HTMLInputElement>
const Input = ({ className = '', joined, label, hideLabel, error, errorMsg, ...attrs }: InputProps) => (
  <>
    {label ? (
      <Label $hidden={hideLabel} htmlFor={attrs.id}>
        {label}
      </Label>
    ) : null}
    <InputSC className={classes(className, joined && 'joined', (errorMsg || error) && 'errorField')} {...attrs} />
    <ErrorMsg>{errorMsg}</ErrorMsg>
  </>
)

const separator = '-'

const numDigits = (str = '', num = 10) => {
  const digits = str.match(/(\d)/g) || []
  return digits.slice(0, num)
}

const formatPhone = (str = '') => {
  const digits = numDigits(str)
  const formatted = []
  const len = str.length

  digits.forEach((digit, i) => {
    formatted.push(digit)

    if ((i === 2 && len !== 3) || (i === 5 && len !== 7)) {
      formatted.push(separator)
    }
  })

  return formatted.join('')
}

const isValidPhone = num => num.match(/\d+/g).join('').length === 10

const Phone = ({ onChange, ...attrs }) => (
  <Input
    {...attrs}
    minLength={10}
    type="tel"
    onChange={evt => {
      const { target } = evt
      const { value } = target
      const formatted = formatPhone(value)
      const end = target.selectionEnd
      const insertMode = end !== value.length

      target.value = formatted

      if (insertMode) {
        const cursorPosition = formatted[end - 1] === separator ? end + 1 : end
        target.selectionStart = cursorPosition
        target.selectionEnd = cursorPosition
      }
      if (onChange) {
        onChange(evt)
      }
    }}
  />
)

const ZipCode = props => (
  <Input
    {...props}
    onChange={evt => {
      const { target } = evt
      const { value } = target
      let newVal = value

      if (newVal.length > 5) {
        newVal = target.value.slice(0, -1)
        target.value = newVal
      }

      props.onChange(newVal)
    }}
  />
)

type FieldsetProps = {
  className?: string
  label?: string
  id?: string
  children: ReactNode
} & HTMLAttributes<HTMLDivElement>
const Fieldset = ({ className, label, id, children, ...attrs }: FieldsetProps) => (
  <div role="group" id={id} aria-labelledby={`${id}-legend`} {...attrs} className={className}>
    {label ? <Label id={`${id}-legend`}>{label}</Label> : null}
    {children}
  </div>
)

const RadioList = ({ labelId, options, name, value, onChange }) => (
  <div aria-labelledby={labelId} role="group">
    {options.map(option => {
      const id = `${name}_${option.value}`
      return (
        <RadioLabel htmlFor={id} key={id}>
          <Radio
            type="radio"
            name={name}
            value={option.value}
            id={id}
            checked={value === option.value}
            onChange={() => onChange(option)}
          />
          <span>{option.label}</span>
        </RadioLabel>
      )
    })}
  </div>
)

export default Input
export { Button, Select, Label, Phone, ZipCode, Fieldset, isValidPhone, RadioList }
